/**
 * PrivatDetailPage (F1) — ein privates Dokument ansehen & pflegen.
 *
 * Vorschau + Titel + Tags + Notiz. Ist der reservierte Tag „Garantie" gesetzt,
 * erscheint der Garantie-Block (Kaufdatum, Händler, Preis, Dauer → Ablaufdatum
 * + Ampel). Speichern per PATCH, Löschen entfernt Datei + Datensatz.
 *
 * Teilen läuft über das Telefon, nicht über die Cloud: das PDF wird geholt und
 * per Web-Share-API an Mail, Telegram, WhatsApp … weitergereicht. Kein
 * öffentlicher Freigabelink — private Dokumente verlassen die Cloud nur, wenn
 * der Nutzer sie selbst weitergibt.
 *
 * Zwei Fallen, die hier fest verbaut sind:
 *
 * 1. `navigator.share` braucht eine *frische* Nutzeraktivierung. Wer das PDF erst
 *    im Klick-Handler holt (`await fetch` + `await blob`), hat sie verbraucht,
 *    bevor er teilt — der Browser antwortet mit NotAllowedError, und je größer
 *    die Datei, desto sicherer. Deshalb liegt das PDF vorgeladen bereit und der
 *    Klick ruft `share()` ohne ein einziges `await` davor.
 * 2. Ein `window.open` auf die sitzungsgeschützte PDF-Adresse führt aus der
 *    Home-Bildschirm-App ins Leere. Angesehen wird deshalb in der App: der Server
 *    liefert je Seite ein Bild. Ein PDF im iframe zeigt auf dem Telefon nur die
 *    erste Seite — ein Bild zeigt immer die richtige.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useVollbildZurueck } from "../lib/vollbild";
import ClaraHinweis from "../components/beleg/ClaraHinweis";

const GARANTIE = "Garantie";
const TAG_VORSCHLAEGE = ["Garantie", "Arzt", "Vertrag", "Rechnung privat", "Sonstiges"];

function addMonths(iso: string, months: number): string {
  if (!iso || !months) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}
function fmtDatum(iso: string): string {
  if (!iso) return "";
  const [y, m, dd] = iso.split("-");
  return `${dd}.${m}.${y}`;
}
function status(ende: string): { farbe: string; text: string } | null {
  if (!ende) return null;
  const tage = Math.round((new Date(ende + "T00:00:00").getTime() - Date.now()) / 86400000);
  if (tage < 0) return { farbe: "#e0705a", text: "abgelaufen" };
  if (tage <= 60) return { farbe: "#e0a53f", text: `läuft in ${tage} Tagen ab` };
  return { farbe: "#3fae7a", text: "Garantie aktiv" };
}

const inp: React.CSSProperties = {
  width: "100%", padding: "9px 12px", borderRadius: 10, border: "1px solid var(--m-line, #333)",
  background: "var(--m-card, #1a1a1a)", color: "var(--m-ink, #eee)", fontSize: 14.5,
};
const lbl: React.CSSProperties = { fontSize: 12, color: "var(--m-ink-3, #999)", marginBottom: 4, display: "block" };

export default function PrivatDetailPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  // Meldung aus dem Erfassen (ältere App-Stände): Upload ging durch, Lesen nicht.
  const kiFehlerVomErfassen = (loc.state as { kiFehler?: string } | null)?.kiFehler || "";

  const q = useQuery({
    queryKey: ["privat", id],
    queryFn: () => api.getPrivat(id),
    enabled: !!id,
    // Solange Clara liest, alle 2,5 s nachsehen — dann füllen sich die Felder von selbst.
    refetchInterval: (qq) => (qq.state.data?.ki_laeuft ? 2500 : false),
  });
  const dok = q.data;
  const claraLiest = !!dok?.ki_laeuft;
  const tagsQ = useQuery({ queryKey: ["privat-tags"], queryFn: () => api.listPrivatTags(), staleTime: 60 * 1000 });

  const [titel, setTitel] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [notiz, setNotiz] = useState("");
  const [dokumentdatum, setDokumentdatum] = useState("");
  const [haendler, setHaendler] = useState("");
  const [preis, setPreis] = useState("");
  const [garantieMonate, setGarantieMonate] = useState("24");
  const [seriennummer, setSeriennummer] = useState("");
  const [neuerTag, setNeuerTag] = useState("");
  const [full, setFull] = useState(false);
  const [seite, setSeite] = useState(0);
  const [teilenFehler, setTeilenFehler] = useState("");

  // Vorgeladenes PDF — siehe Kopfkommentar Punkt 1. `bereit` steuert nur die
  // Beschriftung; gefehlt hat es nie an der Datei, sondern an der Aktivierung.
  const pdfRef = useRef<Blob | null>(null);
  const [pdfBereit, setPdfBereit] = useState(false);

  const seitenQ = useQuery({
    queryKey: ["privat-seiten", id],
    queryFn: () => api.privatSeiten(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
  const seitenZahl = Math.max(1, seitenQ.data?.seiten ?? 1);

  useEffect(() => {
    if (!id) return;
    let abgebrochen = false;
    const ctrl = new AbortController();
    void (async () => {
      try {
        const r = await fetch(api.privatPdfUrl(id), { credentials: "include", signal: ctrl.signal });
        if (!r.ok) return;
        const blob = await r.blob();
        if (abgebrochen) return;
        pdfRef.current = blob;
        setPdfBereit(true);
      } catch {
        // Kein Drama: der Teilen-Knopf holt es dann selbst nach.
      }
    })();
    return () => {
      abgebrochen = true;
      ctrl.abort();
      pdfRef.current = null;
    };
  }, [id]);

  useEffect(() => {
    if (!dok) return;
    setTitel(dok.titel);
    setTags(dok.tags);
    setNotiz(dok.notiz);
    setDokumentdatum(dok.dokumentdatum);
    setHaendler(dok.haendler);
    setPreis(dok.preis ? String(dok.preis).replace(".", ",") : "");
    setGarantieMonate(dok.garantie_monate != null ? String(dok.garantie_monate) : "24");
    setSeriennummer(dok.seriennummer);
  }, [dok]);

  const hatGarantie = tags.includes(GARANTIE);
  const tagVorschlaege = useMemo(() => {
    const s = new Set<string>([...TAG_VORSCHLAEGE, ...(tagsQ.data ?? [])]);
    tags.forEach((t) => s.delete(t));
    return Array.from(s);
  }, [tagsQ.data, tags]);
  const endeVorschau = useMemo(
    () => (hatGarantie ? addMonths(dokumentdatum, parseInt(garantieMonate || "0", 10)) : ""),
    [hatGarantie, dokumentdatum, garantieMonate],
  );
  const amp = status(endeVorschau);

  /** Der Formularstand als PATCH-Rumpf — von Speichern UND von Claras Lesen genutzt. */
  const formStand = useCallback(() => {
    const preisNum = preis.trim() ? Number(preis.replace(/\./g, "").replace(",", ".")) : undefined;
    return {
      titel: titel.trim(),
      tags,
      notiz,
      dokumentdatum,
      haendler: hatGarantie ? haendler.trim() : "",
      preis: hatGarantie ? (Number.isFinite(preisNum as number) ? preisNum : undefined) : undefined,
      garantie_monate: hatGarantie ? (parseInt(garantieMonate || "0", 10) || null) : null,
      seriennummer: hatGarantie ? seriennummer.trim() : "",
    };
  }, [titel, tags, notiz, dokumentdatum, hatGarantie, haendler, preis, garantieMonate, seriennummer]);

  const saveM = useMutation({
    mutationFn: () => api.patchPrivat(id, formStand()),
    onSuccess: (d) => {
      qc.setQueryData(["privat", id], d);
      qc.invalidateQueries({ queryKey: ["privat"] });
      nav("/privat");
    },
  });

  const delM = useMutation({
    mutationFn: () => api.deletePrivat(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["privat"] });
      nav("/privat");
    },
  });

  const dateiName = useCallback(
    () => `${(titel.trim() || "dokument").replace(/[^\wÄÖÜäöüß\-. ]+/g, "_").slice(0, 60)}.pdf`,
    [titel],
  );

  /** Letzter Ausweg, wenn das Telefon nicht teilen will oder darf. */
  const herunterladen = useCallback((blob: Blob, name: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 10000);
  }, []);

  /**
   * PDF über die Teilen-Funktion des Telefons weitergeben.
   *
   * Bewusst NICHT `async`: zwischen dem Antippen und `share()` darf kein `await`
   * liegen, sonst ist die Nutzeraktivierung verfallen und der Browser lehnt mit
   * NotAllowedError ab (siehe Kopfkommentar). Das PDF liegt vorgeladen bereit.
   */
  const teilen = () => {
    setTeilenFehler("");
    const blob = pdfRef.current;
    if (!blob) {
      setTeilenFehler("Das Dokument wird noch geladen — gleich noch einmal antippen.");
      return;
    }
    const name = dateiName();
    const file = new File([blob], name, { type: "application/pdf" });
    const n = navigator as Navigator & {
      canShare?: (d: ShareData) => boolean;
      share?: (d: ShareData) => Promise<void>;
    };
    if (!n.share || !n.canShare?.({ files: [file] })) {
      // Browser ohne Datei-Teilen (meist Desktop): herunterladen, dann von
      // Hand weitergeben — besser als ein Knopf, der nichts tut.
      herunterladen(blob, name);
      return;
    }
    n.share({ files: [file], title: titel.trim() || "Privates Dokument" }).catch((e: Error) => {
      // Abbruch im Teilen-Dialog ist kein Fehler.
      if (e?.name === "AbortError") return;
      // Das Telefon verweigert das Teilen (z. B. Aktivierung doch verfallen) —
      // dann wenigstens die Datei herausgeben statt eine englische Meldung zeigen.
      if (e?.name === "NotAllowedError") {
        herunterladen(blob, name);
        return;
      }
      setTeilenFehler(e?.message || "Teilen fehlgeschlagen.");
    });
  };

  /**
   * Clara (noch einmal) über das Dokument lesen lassen — im Hintergrund wie
   * beim Erfassen. Füllt serverseitig nur leere Felder, überschreibt also
   * nichts von Hand Eingetragenes.
   */
  const kiM = useMutation({
    // Erst den Formularstand festschreiben, dann lesen lassen: Clara füllt
    // nur LEERE Felder — ohne diesen Schritt gälte ein getippter, aber noch
    // nicht gespeicherter Titel als leer und würde überschrieben.
    mutationFn: async () => {
      await api.patchPrivat(id, formStand());
      return api.privatLesenLassen(id);
    },
    onSuccess: (d) => {
      qc.setQueryData(["privat", id], d);
      qc.invalidateQueries({ queryKey: ["privat"] });
    },
  });

  const addTag = (t: string) => {
    const v = t.trim();
    if (v && !tags.includes(v)) setTags([...tags, v]);
    setNeuerTag("");
  };
  const removeTag = (t: string) => setTags(tags.filter((x) => x !== t));

  if (q.isLoading) return <div className="m-app" style={{ padding: 24, color: "var(--m-ink-3)" }}>Lade…</div>;
  if (q.isError || !dok) return <div className="m-app" style={{ padding: 24, color: "tomato" }}>Nicht gefunden.</div>;

  return (
    <div className="m-app" style={{ minHeight: "100vh", paddingBottom: 96 }}>
      <div style={{ padding: "calc(env(safe-area-inset-top) + 12px) 16px 8px" }}>
        <button onClick={() => nav("/privat")} style={{ background: "none", border: "none", color: "var(--m-ink-3, #888)", fontSize: 13, padding: 0, cursor: "pointer" }}>
          ‹ Private Dokumente
        </button>
      </div>

      {/* Vorschau */}
      <div style={{ padding: "4px 16px 12px" }}>
        <img
          src={api.privatThumbUrl(id, 700)}
          alt={titel}
          onClick={() => { setSeite(0); setFull(true); }}
          style={{ width: "100%", maxHeight: 260, objectFit: "contain", background: "#fff", borderRadius: 12, border: "1px solid var(--m-line, #333)", cursor: "zoom-in" }}
        />
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => { setSeite(0); setFull(true); }}
            style={{ flex: 1, minHeight: 44, padding: "11px 14px", borderRadius: 12, border: "1px solid var(--m-line, #333)", background: "transparent", color: "var(--m-ink, #eee)", fontSize: 14.5, fontWeight: 600, cursor: "pointer" }}
          >
            {seitenZahl > 1 ? `Öffnen · ${seitenZahl} Seiten` : "Öffnen"}
          </button>
          <button
            onClick={teilen}
            style={{ flex: 1, minHeight: 44, padding: "11px 14px", borderRadius: 12, border: "1px solid var(--m-line, #333)", background: "transparent", color: "var(--m-ink, #eee)", fontSize: 14.5, fontWeight: 600, cursor: "pointer" }}
          >
            {pdfBereit ? "Teilen" : "Lädt…"}
          </button>
        </div>
        {teilenFehler && (
          <div style={{ marginTop: 6, color: "tomato", fontSize: 12.5 }}>{teilenFehler}</div>
        )}
      </div>

      {/* Clara liest gerade bzw. konnte nicht lesen */}
      <div style={{ padding: "0 16px 12px" }}>
        <ClaraHinweis
          laeuft={claraLiest}
          fehler={dok.ki_fehler}
          titel="Clara liest das Dokument …"
          was="Titel, Datum und Garantie"
          zusatz="Du kannst die Seite ruhig verlassen."
          onNochmal={() => kiM.mutate()}
        />
      </div>

      {/* Fallback: Dokument ohne Titel, das Clara (noch) nicht gelesen hat. */}
      {(kiFehlerVomErfassen || !titel.trim()) && !claraLiest && !dok.ki_fehler && !kiM.isPending && (
        <div style={{ margin: "0 16px 12px", padding: "11px 13px", borderRadius: 12, border: "1px solid rgba(224,165,63,0.4)", background: "rgba(224,165,63,0.08)", fontSize: 12.5, color: "var(--m-ink-2, #ccc)", lineHeight: 1.45 }}>
          {kiFehlerVomErfassen
            ? `Das Dokument ist sicher gespeichert, aber Clara konnte es noch nicht lesen: ${kiFehlerVomErfassen} Titel selbst eintragen oder unten noch einmal lesen lassen.`
            : "Dieses Dokument hat noch keinen Titel — beim Erfassen ist die Erkennung nicht durchgelaufen. Titel selbst eintragen oder unten noch einmal von Clara lesen lassen."}
        </div>
      )}

      {/* Während Clara liest, ist die Maske gesperrt — sonst überschreibt ihr
          Ergebnis gleich wieder, was man gerade tippt. */}
      <div
        aria-busy={claraLiest}
        style={{
          padding: "0 16px", display: "flex", flexDirection: "column", gap: 14,
          opacity: claraLiest ? 0.55 : 1, pointerEvents: claraLiest ? "none" : undefined, transition: "opacity .2s",
        }}
      >
        <div>
          <label style={lbl}>Titel</label>
          <input style={inp} value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="z. B. Bosch Waschmaschine" />
          <button
            onClick={() => kiM.mutate()}
            disabled={kiM.isPending || claraLiest}
            style={{ marginTop: 6, minHeight: 44, background: "none", border: "none", color: "var(--m-accent, #6db04a)", fontSize: 12.5, fontWeight: 600, cursor: "pointer", padding: 0, textAlign: "left" }}
          >
            {kiM.isPending || claraLiest ? "Clara liest das Dokument …" : "✨ Von Clara lesen lassen"}
          </button>
          <div style={{ fontSize: 11.5, color: "var(--m-ink-3, #888)", lineHeight: 1.4 }}>
            Füllt nur leere Felder — von Hand Eingetragenes bleibt stehen.
          </div>
          {kiM.isError && (
            <div style={{ marginTop: 5, color: "tomato", fontSize: 12.5 }}>
              {(kiM.error as Error)?.message || "Clara konnte das Dokument nicht lesen."}
            </div>
          )}
        </div>

        {/* Tags */}
        <div>
          <label style={lbl}>Tags</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
            {tags.map((t) => (
              <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, padding: "4px 8px", borderRadius: 999, background: t === GARANTIE ? "rgba(224,165,63,0.18)" : "var(--m-line, #2a2a2a)", color: "var(--m-ink, #eee)" }}>
                {t}
                <span onClick={() => removeTag(t)} style={{ cursor: "pointer", color: "var(--m-ink-3, #999)" }}>✕</span>
              </span>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              style={inp}
              value={neuerTag}
              onChange={(e) => setNeuerTag(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(neuerTag); } }}
              placeholder="Tag hinzufügen…"
              list="privat-tag-vorschlaege"
            />
            <datalist id="privat-tag-vorschlaege">
              {tagVorschlaege.map((t) => <option key={t} value={t} />)}
            </datalist>
            <button onClick={() => addTag(neuerTag)} style={{ flexShrink: 0, padding: "0 14px", borderRadius: 10, border: "1px solid var(--m-line, #333)", background: "transparent", color: "var(--m-ink, #eee)", fontSize: 18, cursor: "pointer" }}>＋</button>
          </div>
          {!hatGarantie && (
            <button onClick={() => addTag(GARANTIE)} style={{ marginTop: 6, background: "none", border: "none", color: "#e0a53f", fontSize: 12.5, cursor: "pointer", padding: 0 }}>
              + Tag „Garantie" — Ablaufdatum verwalten
            </button>
          )}
        </div>

        {/* Garantie-Block */}
        {hatGarantie && (
          <div style={{ padding: "12px 13px", borderRadius: 12, border: "1px solid rgba(224,165,63,0.4)", background: "rgba(224,165,63,0.06)", display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#e0a53f" }}>Garantie</div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Kaufdatum</label>
                <input type="date" style={inp} value={dokumentdatum} onChange={(e) => setDokumentdatum(e.target.value)} />
              </div>
              <div style={{ width: 110 }}>
                <label style={lbl}>Dauer (Mon.)</label>
                <input type="number" inputMode="numeric" style={inp} value={garantieMonate} onChange={(e) => setGarantieMonate(e.target.value)} />
              </div>
            </div>
            {endeVorschau && amp && (
              <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: amp.farbe, fontWeight: 600 }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: amp.farbe }} />
                Ablauf {fmtDatum(endeVorschau)} · {amp.text}
              </div>
            )}
            <div>
              <label style={lbl}>Händler</label>
              <input style={inp} value={haendler} onChange={(e) => setHaendler(e.target.value)} placeholder="z. B. Media Markt" />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Preis (€)</label>
                <input inputMode="decimal" style={inp} value={preis} onChange={(e) => setPreis(e.target.value)} placeholder="0,00" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Seriennummer</label>
                <input style={inp} value={seriennummer} onChange={(e) => setSeriennummer(e.target.value)} />
              </div>
            </div>
          </div>
        )}

        <div>
          <label style={lbl}>Notiz</label>
          <textarea style={{ ...inp, minHeight: 60, resize: "vertical" }} value={notiz} onChange={(e) => setNotiz(e.target.value)} />
        </div>

        {(saveM.isError || delM.isError) && (
          <div style={{ color: "tomato", fontSize: 12.5 }}>{((saveM.error || delM.error) as Error)?.message}</div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 2 }}>
          <button
            onClick={() => saveM.mutate()}
            disabled={saveM.isPending || claraLiest}
            style={{ flex: 1, padding: "13px", borderRadius: 12, border: "none", background: "var(--m-accent, #6db04a)", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" }}
          >
            {saveM.isPending ? "Speichere…" : claraLiest ? "Clara liest …" : "Speichern"}
          </button>
          <button
            onClick={() => { if (window.confirm("Dieses private Dokument löschen?")) delM.mutate(); }}
            disabled={delM.isPending}
            style={{ padding: "13px 16px", borderRadius: 12, border: "1px solid var(--m-line, #333)", background: "transparent", color: "#e0705a", fontSize: 14, cursor: "pointer" }}
          >
            Löschen
          </button>
        </div>
      </div>

      {full && (
        <Betrachter
          id={id}
          titel={titel}
          seite={seite}
          seitenZahl={seitenZahl}
          onSeite={setSeite}
          onClose={() => setFull(false)}
        />
      )}
    </div>
  );
}

/**
 * Vollbild-Betrachter — blättert seitenweise durch Serverbilder.
 *
 * Bewusst Bilder statt des PDF: ein eingebettetes PDF zeigt auf dem Telefon nur
 * die erste Seite, und ein neues Fenster auf die sitzungsgeschützte Adresse
 * scheitert aus der Home-Bildschirm-App. Das echte PDF gibt es über „Teilen".
 */
function Betrachter({
  id, titel, seite, seitenZahl, onSeite, onClose,
}: {
  id: string;
  titel: string;
  seite: number;
  seitenZahl: number;
  onSeite: (n: number) => void;
  onClose: () => void;
}) {
  const zurueck = seite > 0;
  const vor = seite < seitenZahl - 1;

  // Nächste Seite still vorladen, damit das Blättern nicht flackert.
  useEffect(() => {
    if (!vor) return;
    const img = new Image();
    img.src = api.privatThumbUrl(id, 1400, seite + 1);
  }, [id, seite, vor]);

  // Esc + Zurueck-Geste schliessen (der Haken), Pfeile blaettern.
  useVollbildZurueck(true, onClose);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && zurueck) onSeite(seite - 1);
      if (e.key === "ArrowRight" && vor) onSeite(seite + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onSeite, seite, zurueck, vor]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 1100, display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "calc(env(safe-area-inset-top) + 10px) 14px 8px", color: "#fff", flexShrink: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.85 }}>
          {seitenZahl > 1 ? `Seite ${seite + 1} von ${seitenZahl}` : titel || "Dokument"}
        </span>
        <button
          onClick={onClose}
          aria-label="Schließen"
          style={{ minWidth: 44, minHeight: 44, background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer" }}
        >
          ✕
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 12px", overflow: "auto" }}>
        <img
          key={seite}
          src={api.privatThumbUrl(id, 1400, seite)}
          alt={seitenZahl > 1 ? `${titel} — Seite ${seite + 1}` : titel}
          style={{ maxWidth: "100%", objectFit: "contain", background: "#fff", borderRadius: 8 }}
        />
      </div>

      {seitenZahl > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, padding: "10px 14px calc(env(safe-area-inset-bottom) + 14px)", flexShrink: 0 }}>
          <button onClick={() => onSeite(seite - 1)} disabled={!zurueck} style={blaettern(zurueck)}>‹ Zurück</button>
          <button onClick={() => onSeite(seite + 1)} disabled={!vor} style={blaettern(vor)}>Weiter ›</button>
        </div>
      )}
    </div>
  );
}

const blaettern = (aktiv: boolean): React.CSSProperties => ({
  minWidth: 110, minHeight: 44, padding: "11px 18px", borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.08)",
  color: aktiv ? "#fff" : "rgba(255,255,255,0.3)", fontSize: 14.5, fontWeight: 600,
  cursor: aktiv ? "pointer" : "default",
});
